import { useEffect,useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import * as XLSX from 'xlsx';

import { LayoutQuestion } from '../LayoutQuestion';

export default function MatrixQuestionEdit(props) {
  const [isMultipleChoice, setIsMultipleChoice] = useState(false);
  const [optionType, setOptionType] = useState('fixed'); // 'fixed' o 'dynamic'
  const [excelFileName, setExcelFileName] = useState('');
  const [excelOptions, setExcelOptions] = useState([]);
  const [question, setQuestion] = useState({
    title: '',
    description: '',
    rows: [''],
    columns: [''],
    responses: {},
  });

  const addRow = () => {
    setQuestion((prev) => ({
      ...prev,
      rows: [...prev.rows, ''],
    }));

    props.setQuestion((prev) => ({
      ...prev,
      matrixOption: {
        ...prev.matrixOption,
        options: [...(prev.matrixOption.options || []), ''],
      },
    }));
  };

  const addColumn = () => {
    setQuestion((prev) => ({
      ...prev,
      columns: [...prev.columns, ''],
    }));

    props.setQuestion((prev) => ({
      ...prev,
      matrixOption: {
        ...prev.matrixOption,
        values: [...(prev.matrixOption.values || []), ''],
      },
    }));
  };

  const removeRow = (index) => {
    if (props.question.matrixOption.options.length > 1) {
      setQuestion((prev) => ({
        ...prev,
        rows: prev.rows.filter((_, i) => i !== index),
      }));

      props.setQuestion((prev) => ({
        ...prev,
        matrixOption: {
          ...prev.matrixOption,
          options: prev.matrixOption.options.filter((_, i) => i !== index),
        },
      }));
    }
  };

  const removeColumn = (index) => {
    if (props.question.matrixOption.values.length > 1) {
      setQuestion((prev) => ({
        ...prev,
        columns: prev.columns.filter((_, i) => i !== index),
      }));
      props.setQuestion((prev) => ({
        ...prev,
        matrixOption: {
          ...prev.matrixOption,
          values: prev.matrixOption.values.filter((_, i) => i !== index),
        },
      }));
    }
  };

  const updateRow = (index, value) => {
    setQuestion((prev) => ({
      ...prev,
      rows: prev.rows.map((row, i) => (i === index ? value : row)),
    }));

    props.setQuestion((prev) => {
      const newValues = [...(prev.matrixOption.options || [])];
      newValues[index] = value;
      return {
        ...prev,
        matrixOption: {
          ...prev.matrixOption,
          options: newValues,
        },
      };
    });
  };

  const updateColumn = (index, value) => {
    setQuestion((prev) => ({
      ...prev,
      columns: prev.columns.map((col, i) => (i === index ? value : col)),
    }));

    props.setQuestion((prev) => {
      const newValues = [...(prev.matrixOption.values || [])];
      newValues[index] = value;
      return {
        ...prev,
        matrixOption: {
          ...prev.matrixOption,
          values: newValues,
        },
      };
    });
  };

  const handleResponseChange = (rowIndex, columnIndex) => {
    setQuestion((prev) => ({
      ...prev,
      responses: {
        ...prev.responses,
        [rowIndex]: columnIndex.toString(),
      },
    }));
  };

  const handleDynamicResponseChange = (rowIndex, value) => {
    setQuestion((prev) => ({
      ...prev,
      responses: {
        ...prev.responses,
        [rowIndex]: value,
      },
    }));
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          // Extraer opciones de la primera columna (excluyendo encabezado si existe)
          const options = jsonData
            .slice(1) // Omitir primera fila (encabezado)
            .map((row) => row[0])
            .filter((option) => option && option.toString().trim() !== '');

          setExcelOptions(options);
          setExcelFileName(file.name);
          // Limpiar respuestas al cargar nuevo archivo
          setQuestion((prev) => ({ ...prev, responses: {} }));

          props.setQuestion((prev) => ({
            ...prev,
            matrixOption: {
              ...prev.matrixOption,
              options: options,
            },
          }));
        } catch (error) {
          console.error('Error al leer el archivo Excel:', error);
          alert(
            'Error al procesar el archivo Excel. Asegúrate de que sea un archivo válido.'
          );
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const tableHeaderStyle = {
    border: '1px solid #ccc',
    padding: '12px',
    backgroundColor: '#f5f5f5',
    minWidth: '120px',
    textAlign: 'center',
  };

  const tableCellStyle = {
    border: '1px solid #ccc',
    padding: '10px',
    textAlign: 'center',
  };

  const tableElementCellStyle = {
    ...tableCellStyle,
    textAlign: 'left',
    fontWeight: 'bold',
  };

  useEffect(() => {
    props.setQuestion((prev) => ({
      ...prev,
      matrixOption: {
        ...prev.matrixOption,
        matrixType: props.question.matrixOption?.matrixType,
      },
    }));
  }, [optionType]);

  useEffect(() => {
    props.setQuestion((prev) => ({
      ...prev,
      matrixOption: {
        ...prev.matrixOption,
        inputType: props.question.matrixOption?.inputType,
      },
    }));
  }, [isMultipleChoice]);

  useEffect(() => {
    if (
      props.question.matrixOption?.matrixType === 'dynamic' &&
      Array.isArray(props.question.matrixOption.options)
    ) {
      setExcelOptions(props.question.matrixOption.options);
      setExcelFileName('archivo_excel_previamente_cargado.xlsx'); // ← tú puedes cambiar esto o incluso guardarlo en el modelo si lo deseas
    }
  }, []);
  return (
    <Box sx={{ maxWidth: 1000, margin: '0 auto', padding: 4 }}>
      <Box
        sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2, mb: 2 }}
      >
        {/* Nuevo select para tipo de opciones */}
        <FormControl size="small">
          <InputLabel id="option-type-label">Tipo de opciones</InputLabel>
          <Select
            labelId="option-type-label"
            value={props.question.matrixOption?.matrixType || 'fixed'}
            onChange={(e) => {
              const newValue = e.target.value;
              setOptionType(newValue); // Cambiar el estado local
              props.setQuestion((prev) => ({
                ...prev,
                matrixOption: {
                  ...(prev.matrixOption || {}), // Proteger también aquí
                  matrixType: newValue,
                },
                responses: {},
              }));
              if (newValue === 'fixed') {
                setExcelOptions([]); // Limpiar opciones de Excel
              }
            }}
            label="Tipo de opciones"
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="fixed">Opciones fijas</MenuItem>
            <MenuItem value="dynamic">Opciones dinámicas</MenuItem>
          </Select>
        </FormControl>

        {/* Select para tipo de selección - siempre visible */}
        <FormControl size="small">
          <InputLabel id="selection-type-label">Tipo de selección</InputLabel>
          <Select
            labelId="input-type-label"
            value={props.question.matrixOption?.inputType || 'single'}
            onChange={(e) => {
              const newValue = e.target.value;
              setIsMultipleChoice(newValue === 'multiple'); // Actualiza estado local
              props.setQuestion((prev) => ({
                ...prev,
                matrixOption: {
                   ...(prev.matrixOption || {}), 
                  inputType: newValue, // Actualiza inputType en matrixOption
                },
                responses: {}, // Limpia respuestas
              }));
            }}
            label="Tipo de entrada"
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="single">Selección única (Radio)</MenuItem>
            <MenuItem value="multiple">Selección múltiple (Checkbox)</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Input para subir archivo Excel - solo visible para opciones dinámicas */}
      {props.question.matrixOption?.matrixType === 'dynamic' && (
        <Box sx={{ mb: 3 }}>
          <Card>
            <CardHeader title="Cargar opciones desde Excel" />
            <CardContent>
              {excelOptions.length === 0 ? (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}
                >
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadFileIcon />}
                  >
                    Seleccionar archivo Excel
                    <input
                      type="file"
                      hidden
                      accept=".xlsx,.xls"
                      onChange={handleFileUpload}
                    />
                  </Button>
                  <Typography
                    variant="body2"
                    color={props.helperText.matrix ? 'error' : 'text.secondary'}
                    sx={{ ml: '2px' }}
                  >
                    {props.helperText.matrix
                      ? props.helperText.matrix
                      : 'Las opciones se tomarán de la primera columna del archivo'}
                  </Typography>
                </Box>
              ) : (
                <>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Archivo cargado: <strong>{excelFileName}</strong>
                    <br />
                    Se cargaron {excelOptions.length} opciones:{' '}
                    {excelOptions.slice(0, 3).join(', ')}
                    {excelOptions.length > 3 && '...'}
                  </Alert>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      setExcelOptions([]);
                      setExcelFileName('');
                      setQuestion((prev) => ({ ...prev, responses: {} }));

                      props.setQuestion((prev) => ({
                        ...prev,
                        matrixOption: {
                          ...prev.matrixOption,
                          options: [],
                        },
                      }));
                    }}
                  >
                    Eliminar archivo cargado
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title={
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  {optionType === 'fixed'
                    ? 'Filas (Elementos a evaluar)'
                    : 'Filas (Solo para referencia)'}
                  {optionType === 'fixed' && (
                    <Button onClick={addRow} size="small">
                      <AddIcon fontSize="small" />
                    </Button>
                  )}
                </Box>
              }
            />
            <CardContent>
              {props.question.matrixOption?.matrixType === 'fixed' ? (
                props.question.matrixOption.options.map((row, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      value={props.question.matrixOption.options[index]}
                      placeholder={`Elemento ${index + 1}`}
                      onChange={(e) => updateRow(index, e.target.value)}
                      error={props.errorMessage.matrixRows?.[index] || false}
                      helperText={
                        props.errorMessage.matrixRows?.[index]
                          ? 'Este campo no puede ir vacío'
                          : ''
                      }
                    />
                    <Button
                      onClick={() => removeRow(index)}
                      size="small"
                      variant="outlined"
                      disabled={
                        props.question.matrixOption.options.length === 1
                      }
                    >
                      <CloseIcon fontSize="small" />
                    </Button>
                  </Box>
                ))
              ) : excelOptions.length > 0 ? (
                <Alert severity="info">
                  Opciones dinámicas cargadas desde archivo:{' '}
                  {excelOptions.length} opciones
                </Alert>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Para opciones dinámicas, las filas se cargan desde el archivo
                  Excel.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title={
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  Columnas (Criterios de evaluación)
                  <Button onClick={addColumn} size="small">
                    <AddIcon fontSize="small" />
                  </Button>
                </Box>
              }
            />
            <CardContent>
              {props.question.matrixOption?.values?.map((col, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    value={props.question.matrixOption.values[index]}
                    placeholder={`Criterio ${index + 1}`}
                    onChange={(e) => updateColumn(index, e.target.value)}
                    error={props.errorMessage.matrixCols?.[index] || false}
                    helperText={
                      props.errorMessage.matrixCols?.[index]
                        ? 'Este campo no puede ir vacío'
                        : ''
                    }
                  />
                  <Button
                    onClick={() => removeColumn(index)}
                    size="small"
                    variant="outlined"
                    disabled={props.question.matrixOption.values.length === 1}
                  >
                    <CloseIcon fontSize="small" />
                  </Button>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={props.isPreviewQuestion}
        onClose={() => props.setIsPreviewQuestion(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Vista previa de la matriz</DialogTitle>
        <DialogContent dividers>
          <div style={{ overflowX: 'auto' }}>
            {optionType === 'fixed' ? (
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                  <tr>
                    <th style={tableHeaderStyle}>Elementos</th>
                    {question.columns.map((column, index) => (
                      <th key={index} style={tableHeaderStyle}>
                        {column || `Columna ${index + 1}`}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {question.rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      <td style={tableElementCellStyle}>
                        {row || `Fila ${rowIndex + 1}`}
                      </td>
                      {question.columns.map((_, columnIndex) => (
                        <td key={columnIndex} style={tableCellStyle}>
                          {isMultipleChoice ? (
                            <input
                              type="checkbox"
                              checked={
                                Array.isArray(question.responses[rowIndex]) &&
                                question.responses[rowIndex]?.includes(
                                  columnIndex.toString()
                                )
                              }
                              onChange={(e) => {
                                setQuestion((prev) => {
                                  const current = Array.isArray(
                                    prev.responses[rowIndex]
                                  )
                                    ? [...prev.responses[rowIndex]]
                                    : [];

                                  const updated = e.target.checked
                                    ? [...current, columnIndex.toString()]
                                    : current.filter(
                                        (val) => val !== columnIndex.toString()
                                      );

                                  return {
                                    ...prev,
                                    responses: {
                                      ...prev.responses,
                                      [rowIndex]: updated,
                                    },
                                  };
                                });
                              }}
                            />
                          ) : (
                            <RadioGroup
                              row
                              value={question.responses[rowIndex] || ''}
                              onChange={() => {}}
                            >
                              <FormControlLabel
                                value={columnIndex.toString()}
                                control={<Radio disabled />}
                                label=""
                              />
                            </RadioGroup>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              // Vista previa para opciones dinámicas con select en las filas
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                  <tr>
                    <th style={tableHeaderStyle}>Elementos</th>
                    {question.columns.map((column, index) => (
                      <th key={index} style={tableHeaderStyle}>
                        {column || `Columna ${index + 1}`}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Fila para el select con opciones dinámicas */}
                  <tr>
                    <td style={tableElementCellStyle}>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Seleccionar opción</InputLabel>
                        <Select
                          value={question.responses['dynamicOption'] || ''}
                          onChange={(e) =>
                            handleDynamicResponseChange(
                              'dynamicOption',
                              e.target.value
                            )
                          }
                          displayEmpty
                          label="Seleccionar opción"
                        >
                          <MenuItem value="">
                            <em>Seleccionar opción</em>
                          </MenuItem>
                          {excelOptions.map((option, optionIndex) => (
                            <MenuItem key={optionIndex} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </td>
                    {question.columns.map((_, columnIndex) => (
                      <td key={columnIndex} style={tableCellStyle}>
                        {isMultipleChoice ? (
                          <input
                            type="checkbox"
                            checked={
                              Array.isArray(
                                question.responses['dynamicOptionColumns']
                              ) &&
                              question.responses[
                                'dynamicOptionColumns'
                              ]?.includes(columnIndex.toString())
                            }
                            onChange={(e) => {
                              setQuestion((prev) => {
                                const current = Array.isArray(
                                  prev.responses['dynamicOptionColumns']
                                )
                                  ? [...prev.responses['dynamicOptionColumns']]
                                  : [];

                                const updated = e.target.checked
                                  ? [...current, columnIndex.toString()]
                                  : current.filter(
                                      (val) => val !== columnIndex.toString()
                                    );

                                return {
                                  ...prev,
                                  responses: {
                                    ...prev.responses,
                                    dynamicOptionColumns: updated,
                                  },
                                };
                              });
                            }}
                          />
                        ) : (
                          <RadioGroup
                            row
                            value={
                              question.responses['dynamicOptionColumns'] || ''
                            }
                            onChange={(e) => {
                              setQuestion((prev) => ({
                                ...prev,
                                responses: {
                                  ...prev.responses,
                                  dynamicOptionColumns: e.target.value,
                                },
                              }));
                            }}
                          >
                            <FormControlLabel
                              value={columnIndex.toString()}
                              control={<Radio />}
                              label=""
                            />
                          </RadioGroup>
                        )}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            )}
          </div>

          {optionType === 'dynamic' && excelOptions.length === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Debes cargar un archivo Excel para ver las opciones dinámicas.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => props.setIsPreviewQuestion(false)}
            variant="outlined"
          >
            Cerrar vista previa
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
